class Router extends EventTarget {
  constructor() {
    super();
    this._routes = new Map();
    this._paths = new Map();
    this._canonicalPaths = new Map();
    this._currentSlug = null;
    this._isDraft = false;

    document.addEventListener('click', (event) => this._handleLinkClick(event));
    window.addEventListener('popstate', () => this._resolve());
    window.addEventListener('load', () => this._resolve());
  }

  route(slug, componentTag, paths = []) {
    this._routes.set(slug, componentTag);
    const routePaths = paths.length ? paths : [`/${slug}/`];
    this._canonicalPaths.set(slug, routePaths[0]);

    for (const path of routePaths) {
      this._paths.set(this._normalizePath(path), slug);
    }

    return this;
  }

  refresh() {
    this._currentSlug = null;
    this._resolve();
  }

  _resolve() {
    const hashSlug = this._slugFromHash(window.location.hash);
    if (hashSlug) {
      const path = this.pathFor(hashSlug);
      window.history.replaceState({}, '', path);
    }

    const isDraft = this._isDraftPath(window.location.pathname);
    const slug = this._slugFromPath(window.location.pathname) || 'home';

    if (slug === this._currentSlug && isDraft === this._isDraft) return;
    this._currentSlug = slug;
    this._isDraft = isDraft;

    this.dispatchEvent(new CustomEvent('route-changed', {
      detail: { slug, component: this._routes.get(slug) || null, isDraft },
    }));
  }

  _handleLinkClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const anchor = event.composedPath().find((node) => node?.tagName === 'A');
    if (!anchor || anchor.target || anchor.hasAttribute('download')) return;

    const href = anchor.getAttribute('href') || '';
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname.startsWith('/api/')) return;

    const slug = this._slugFromPath(url.pathname);
    if (!slug) return;

    event.preventDefault();
    this.navigate(slug);
  }

  _normalizePath(path) {
    const pathname = path.split('?')[0].split('#')[0] || '/';
    if (pathname === '/') return '/';
    return `/${pathname.replace(/^\/+|\/+$/g, '')}/`;
  }

  _slugFromPath(pathname) {
    const publicPath = this._stripDraftPrefix(pathname);
    const normalized = this._normalizePath(publicPath);
    const mapped = this._paths.get(normalized);
    if (mapped) return mapped;
    const parts = normalized.split('/').filter(Boolean);
    return parts.length === 1 ? decodeURIComponent(parts[0]) : null;
  }

  _isDraftPath(pathname) {
    return this._normalizePath(pathname).startsWith('/drafts/');
  }

  _stripDraftPrefix(pathname) {
    const normalized = this._normalizePath(pathname);
    if (normalized === '/drafts/') return '/';
    if (!normalized.startsWith('/drafts/')) return pathname;
    return normalized.replace(/^\/drafts/, '') || '/';
  }

  _slugFromHash(hash) {
    const slug = hash.replace(/^#\/?/, '').split('/')[0];
    return slug && this._routes.has(slug) ? slug : null;
  }

  navigate(slug) {
    const publicPath = this.pathFor(slug);
    const path = this._isDraft ? `/drafts${publicPath === '/' ? '/' : publicPath}` : publicPath;
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    this._resolve();
  }

  pathFor(slug) {
    return this._canonicalPaths.get(slug) || '/';
  }

  get currentSlug() {
    return this._currentSlug;
  }

  get isDraft() {
    return this._isDraft;
  }
}

export const router = new Router();
